import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { MaskedLine } from "@/components/landing/anim";
import { LogoMark } from "@/components/brand/Logo";

const CONCERT_IMG =
  "https://images.unsplash.com/photo-1765224747205-3c9c23f0553c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxsaXZlJTIwY29uY2VydCUyMGNyb3dkJTIwZGFyayUyMGJsdWV8ZW58MHx8fHwxNzg1NDcyMjUyfDA&ixlib=rb-4.1.0&q=85";

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-500 transition-colors";

const TIER_LABEL = { artist: "Artist plan (£19/yr)", pro: "Pro plan (£29/yr)", label: "Label plan (£99/yr)" };

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Prefer the URL param; fall back to localStorage which survives Google-Auth redirects.
  const paramTier = params.get("tier") || "";
  const storedTier = typeof window !== "undefined" ? (window.localStorage.getItem("noizbloc_pending_tier") || "") : "";
  const preselectedTier = ["artist", "pro", "label"].includes(paramTier)
    ? paramTier
    : (["artist", "pro", "label"].includes(storedTier) ? storedTier : "");
  const { user, loading, setUser } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    account_type: preselectedTier === "label" ? "label" : "artist",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (paramTier) {
      try { window.localStorage.setItem("noizbloc_pending_tier", paramTier); } catch (e) { /* ignore */ }
    }
  }, [paramTier]);

  useEffect(() => {
    if (!loading && user) {
      const t = preselectedTier ? `?tier=${preselectedTier}` : "";
      navigate(`/dashboard${t}`, { replace: true });
    }
  }, [user, loading, navigate, preselectedTier]);

  const signInGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // (The `redirectUrl` below is derived dynamically. `https://auth.emergentagent.com` is the
    // fixed Emergent-managed auth service URL — the playbook uses it verbatim.)
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Enter your name");
    if (!form.email.trim()) return toast.error("Enter your email");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        account_type: form.account_type,
      });
      setUser(data);
      toast.success("Welcome to Noizbloc");
      const t = preselectedTier ? `?tier=${preselectedTier}` : "";
      navigate(`/dashboard${t}`, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "Could not create your account"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#020617] text-white" data-testid="register-page">
      {/* Left: atmospheric imagery */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={CONCERT_IMG} alt="Live concert" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/40 to-[#020617]/10" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2.5">
            <LogoMark className="text-white" />
            <span className="font-display text-xl font-semibold">Noizbloc</span>
          </div>
          <div>
            <h2 className="font-display text-4xl xl:text-5xl font-medium tracking-tight leading-[1.05]">
              <MaskedLine delay={0.1}>Get your</MaskedLine>
              <MaskedLine delay={0.2}>song <span className="text-blue-500">heard.</span></MaskedLine>
            </h2>
            <p className="mt-5 text-slate-400 max-w-sm">
              Create your workspace in seconds. Upload your first release and reach 150+ stores worldwide.
            </p>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <button
          data-testid="register-back-btn"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-10"
        >
          <ArrowLeft size={16} /> Back to home
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-sm w-full"
        >
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-slate-400">Get started</span>
          <h1 className="mt-4 font-display text-4xl font-medium tracking-tight">Create your account</h1>
          <p className="mt-3 text-slate-400">
            Open to every artist and label. No invite required.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-3.5" data-testid="register-form">
            <input
              data-testid="register-name"
              type="text"
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className={inputCls}
              autoComplete="name"
            />
            <input
              data-testid="register-email"
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className={inputCls}
              autoComplete="email"
            />
            <input
              data-testid="register-password"
              type="password"
              placeholder="Password (min. 8 characters)"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className={inputCls}
              autoComplete="new-password"
            />
            <div className="grid grid-cols-2 gap-3 pt-1">
              {["artist", "label"].map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`register-type-${t}`}
                  onClick={() => setForm((f) => ({ ...f, account_type: t }))}
                  className={`rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                    form.account_type === t
                      ? "border-blue-500 bg-blue-500/10 text-white"
                      : "border-white/10 bg-white/[0.02] text-slate-400 hover:text-white hover:border-white/20"
                  }`}
                >
                  {t === "artist" ? "I'm an artist" : "I run a label"}
                </button>
              ))}
            </div>

            <button
              data-testid="register-submit"
              type="submit"
              disabled={busy}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-black font-medium px-6 py-4 hover:bg-slate-200 active:scale-[0.99] transition-all disabled:opacity-60"
            >
              {busy ? <Loader2 size={18} className="animate-spin" /> : "Create account"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-500">
            <div className="h-px flex-1 bg-white/10" />
            <span>or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <button
            data-testid="register-google-btn"
            onClick={signInGoogle}
            className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-white/5 border border-white/10 hover:bg-white/[0.08] text-white font-medium px-6 py-3.5 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </button>

          <p className="mt-8 text-xs text-slate-500 leading-relaxed">
            Already have an account?{" "}
            <button
              data-testid="register-signin-link"
              onClick={() => navigate("/login")}
              className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
            >
              Sign in
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
